import { formatDate, formatPhone, type Claim, type Property } from "@elektr/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Badge, Card, EmptyState, PageHeader, Spinner } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Field, Input, Select } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { useDebounced } from "@/lib/hooks";
import { useT } from "@/lib/i18n";

const STATUS_KEYS = {
  pending: "claim.status_pending",
  approved: "claim.status_approved",
  rejected: "claim.status_rejected",
} as const;
const TONES = { pending: "warning", approved: "success", rejected: "danger" } as const;

export function ClaimsPage() {
  const { t } = useT();
  const [status, setStatus] = useState<"pending" | "approved" | "rejected">("pending");
  const [approving, setApproving] = useState<Claim | null>(null);
  const [rejecting, setRejecting] = useState<Claim | null>(null);

  const query = useQuery({
    queryKey: ["claims", status],
    queryFn: async () =>
      unwrap(await api.GET("/api/claims/", { params: { query: { status, page_size: 50 } } })),
    placeholderData: keepPreviousData,
  });
  const rows = query.data?.results ?? [];

  return (
    <>
      <PageHeader
        title={t("nav.claims")}
        actions={
          <Select
            aria-label={t("props.status")}
            className="w-48"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
          >
            {(["pending", "approved", "rejected"] as const).map((s) => (
              <option key={s} value={s}>
                {t(STATUS_KEYS[s])}
              </option>
            ))}
          </Select>
        }
      />
      <p className="mb-4 text-sm text-muted-foreground">{t("claim.admin_hint")}</p>
      {query.isLoading && <Spinner className="m-8 size-8" />}
      {query.data && rows.length === 0 && <EmptyState>{t("claim.empty")}</EmptyState>}
      <div className="flex flex-col gap-3">
        {rows.map((c) => (
          <Card key={c.id} className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-semibold">{c.address}</div>
                <div className="text-sm text-muted-foreground">
                  {c.user_detail.full_name || "—"} · {formatPhone(c.user_detail.phone)} ·{" "}
                  {formatDate(c.created_at)}
                </div>
              </div>
              <Badge tone={TONES[c.status]}>{t(STATUS_KEYS[c.status])}</Badge>
            </div>
            {c.full_name && (
              <div className="text-sm">
                {t("claim.full_name")}: {c.full_name}
              </div>
            )}
            {c.note && <p className="rounded-lg bg-muted p-2 text-sm">{c.note}</p>}
            {c.admin_note && (
              <p className="text-sm text-muted-foreground">
                {t("claim.admin_note")}: {c.admin_note}
              </p>
            )}
            {c.status === "pending" && (
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setApproving(c)}>
                  {t("claim.approve")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setRejecting(c)}>
                  {t("claim.reject")}
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>
      {approving && <ApproveDialog claim={approving} onClose={() => setApproving(null)} />}
      {rejecting && <RejectDialog claim={rejecting} onClose={() => setRejecting(null)} />}
    </>
  );
}

function useRefreshClaims() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["claims"] });
    void qc.invalidateQueries({ queryKey: ["claims-count"] });
    void qc.invalidateQueries({ queryKey: ["properties"] });
  };
}

function ApproveDialog({ claim, onClose }: { claim: Claim; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const refresh = useRefreshClaims();
  const [text, setText] = useState(claim.address);
  const q = useDebounced(text);
  const [picked, setPicked] = useState<Property | null>(null);
  const [note, setNote] = useState("");

  const found = useQuery({
    queryKey: ["claim-property-search", q],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/properties/", {
          params: { query: { q: q || undefined, page_size: 8 } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const approve = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/claims/{id}/approve/", {
          params: { path: { id: claim.id } },
          body: { property: picked!.id, admin_note: note },
        }),
      ),
    onSuccess: () => {
      toast.success(t("claim.approved_toast"));
      refresh();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t("claim.approve_title")}
      description={t("claim.approve_desc", {
        name: claim.user_detail.full_name || formatPhone(claim.user_detail.phone),
      })}
    >
      <div className="flex flex-col gap-3">
        <Field label={t("claim.find_property")}>
          {(id) => <Input id={id} value={text} onChange={(e) => setText(e.target.value)} />}
        </Field>
        <ul
          className="max-h-56 divide-y divide-border overflow-y-auto rounded-lg border border-border"
          role="radiogroup"
          aria-label={t("claim.approve_title")}
        >
          {found.data?.results.map((p) => (
            <li key={p.id}>
              <label className="flex cursor-pointer items-start gap-3 p-3 text-sm hover:bg-muted">
                <input
                  type="radio"
                  name="property"
                  className="mt-1"
                  checked={picked?.id === p.id}
                  onChange={() => setPicked(p)}
                />
                <span>
                  <strong>
                    {p.address} {p.house_number}
                  </strong>
                  <span className="block text-muted-foreground">
                    {p.district} ·{" "}
                    {p.owner_detail
                      ? `${t("owner.current")}: ${p.owner_detail.full_name || formatPhone(p.owner_detail.phone)}`
                      : t("props.no_owner")}
                  </span>
                </span>
              </label>
            </li>
          ))}
          {found.data?.results.length === 0 && (
            <li className="p-3 text-sm text-muted-foreground">{t("props.empty")}</li>
          )}
        </ul>
        {picked?.owner_detail && picked.owner_detail.id !== claim.user_detail.id && (
          <p role="alert" className="rounded-lg bg-warning/15 p-2 text-sm">
            {t("claim.replace_warning", {
              name: picked.owner_detail.full_name || formatPhone(picked.owner_detail.phone),
            })}
          </p>
        )}
        <Field label={t("claim.admin_note")}>
          {(id) => (
            <Input id={id} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button disabled={!picked || approve.isPending} onClick={() => approve.mutate()}>
            {t("claim.approve")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function RejectDialog({ claim, onClose }: { claim: Claim; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const refresh = useRefreshClaims();
  const [note, setNote] = useState("");
  const reject = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/claims/{id}/reject/", {
          params: { path: { id: claim.id } },
          body: { admin_note: note },
        }),
      ),
    onSuccess: () => {
      refresh();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={t("claim.reject_title")}>
      <div className="flex flex-col gap-3">
        <Field label={t("claim.reject_reason")}>
          {(id) => (
            <Input id={id} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="danger" disabled={reject.isPending} onClick={() => reject.mutate()}>
            {t("claim.reject")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
